import { useState } from 'react';
import { useGetFollowing, useGetUserById } from "@/lib/react-query/queries";
import { Loader } from "@/components/shared";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

type FollowingProps = {
  userId: string;
  showTrustScore?: boolean;
};

const PAGE_SIZE = 10;

type FollowListEntry = {
  follow?: {
    id?: string;
    $id?: string;
    followerId?: string;
    followingId?: string;
  };
  user?: {
    id?: string;
    $id?: string;
    name?: string;
    username?: string;
    imageUrl?: string | null;
    individual?: string;
  } | null;
};

const Following = ({ userId, showTrustScore = false }: FollowingProps) => {
  const [page, setPage] = useState(1);
  const { data: followingData, isLoading } = useGetFollowing(userId, page, PAGE_SIZE);

  const hasMore = followingData ? followingData.total > page * PAGE_SIZE : false;
  const following = followingData?.documents || [];

  const loadMore = () => {
    setPage(prev => prev + 1);
  };

  if (isLoading && page === 1) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader />
      </div>
    );
  }

  if (!following || following.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <div className="w-16 h-16 bg-dark-4 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-light-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
        </div>
        <p className="text-light-3 text-lg font-medium">Not following anyone</p>
        <p className="text-light-3/60 text-sm mt-1">Discover and follow interesting people!</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      <div className="overflow-y-auto flex-1 custom-scrollbar">
        <motion.ul 
          className="flex flex-col"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <AnimatePresence>
            {following.map((follow: FollowListEntry, index: number) => (
              <motion.div
                key={follow.follow?.id ?? follow.follow?.$id ?? follow.user?.id ?? follow.user?.$id ?? index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <FollowingItem 
                  entry={follow}
                  showTrustScore={showTrustScore} 
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.ul>
        
        {hasMore && (
          <div className="p-6 flex justify-center border-t border-dark-4/50">
            <Button 
              onClick={loadMore}
              disabled={isLoading}
              className="shad-button_primary px-8 py-2 bg-gradient-to-r from-primary-500 to-purple-600 hover:from-primary-600 hover:to-purple-700 transition-all duration-300 shadow-lg"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Loading...
                </div>
              ) : (
                'Load More'
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const FollowingItem = ({ entry, showTrustScore }: { 
  entry: FollowListEntry; 
  showTrustScore: boolean 
}) => {
  const followingId = entry.follow?.followingId ?? entry.user?.id ?? entry.user?.$id ?? "";
  const shouldFetchUser = !entry.user && !!followingId;
  const { data: fetchedUser, isLoading } = useGetUserById(shouldFetchUser ? followingId : "");
  const user = entry.user ?? fetchedUser;

  if (shouldFetchUser && isLoading) {
    return (
      <li className="w-full p-4 border-b border-dark-4/30">
        <div className="flex items-center gap-4 animate-pulse">
          <div className="w-12 h-12 rounded-full bg-dark-4"></div>
          <div className="flex-1">
            <div className="h-4 bg-dark-4 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-dark-4 rounded w-1/2"></div>
          </div>
        </div>
      </li>
    );
  }

  if (!user) return null;

  return (
    <motion.li 
      className="w-full hover:bg-dark-3/50 transition-all duration-200 border-b border-dark-4/30 last:border-b-0"
      whileHover={{ backgroundColor: "rgba(255, 255, 255, 0.02)" }}
    >
      <Link 
        to={`/profile/${user.id ?? user.$id}`} 
        className="flex items-center gap-4 w-full p-4 group"
      >
        <div className="relative">
          <img
            src={user.imageUrl || "/assets/icons/profile-placeholder.svg"}
            alt="profile"
            className="w-12 h-12 rounded-full object-cover border-2 border-transparent group-hover:border-primary-500/30 transition-all duration-300"
          />
          <div className="absolute inset-0 rounded-full bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>
        
        <div className="flex-1 min-w-0">
          <p className="body-medium text-light-1 truncate group-hover:text-primary-500 transition-colors duration-200 font-semibold">
            {user.name}
          </p>
          <p className="small-regular text-light-3 truncate opacity-80">
            @{user.username}
          </p>
        </div>
        
        {showTrustScore && user.individual && (
          <motion.span 
            className="text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-dark-4 to-dark-3 text-light-2 border border-dark-3 font-medium"
            whileHover={{ scale: 1.05 }}
          >
            {user.individual === 'business-owner' ? '🏢 Business' : 
             user.individual === 'skilled-employee' ? '💼 Skilled' : 
             '👤 User'}
          </motion.span>
        )}
        
        <svg 
          className="w-5 h-5 text-light-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </Link>
    </motion.li>
  );
};

export default Following;
